import { CORE_APP_ROUTES } from './route-constants';

export interface ToolItem {
  name: string;
  icon?: string;
  path: string;
  comingSoon?: boolean;
  color?: string;
  description: string;
}

export interface ToolGroup {
  name: string;
  items: ToolItem[];
}

/**
 * Generates categorized tool groups for a given suite root path.
 * Used by Sidebar and Landing pages to maintain consistency.
 */
export function getToolGroups(rootPath: string, productionOnly = false): ToolGroup[] {
  const route = CORE_APP_ROUTES.find((r) => r.path === rootPath);
  if (!route || !route.children) {
    return [];
  }

  const groups: ToolGroup[] = [
    {
      name: 'General',
      items: [
        {
          name: 'Suite Overview',
          icon: 'home',
          path: './',
          description: 'Overview of all tools in this suite.',
        },
      ],
    },
  ];

  const children = productionOnly
    ? route.children.filter((child) => child.production)
    : route.children;

  children.forEach((child) => {
    const categoryName = child.category || 'Other';
    let group = groups.find((g) => g.name === categoryName);
    if (!group) {
      group = { name: categoryName, items: [] };
      groups.push(group);
    }

    group.items.push({
      name: child.title,
      icon: child.icon,
      path: './' + child.path.split('/').pop(),
      comingSoon: child.comingSoon,
      color: child.color,
      description: child.description,
    });
  });

  // Custom sorting order for categories
  const order = [
    'General',
    'Essentials',
    'Development',
    'Analysis & UX',
    'Conversion',
    'Modification',
    'Security',
    'Advanced',
    'Encoding',
    'Serialization',
    'Visual',
    'Diagram Utils',
    'Game',
  ];

  return groups.sort((a, b) => {
    const indexA = order.indexOf(a.name);
    const indexB = order.indexOf(b.name);
    return (indexA > -1 ? indexA : 99) - (indexB > -1 ? indexB : 99);
  });
}

/**
 * Gets shell configuration for a specific suite.
 */
export function getSuiteShellConfig(rootPath: string): Record<string, any> {
  const route = CORE_APP_ROUTES.find((r) => r.path === rootPath);
  if (!route) return {};

  return {
    title: route.title,
    description: route.description,
    suiteTitle: route.title,
    suitePath: route.path,
    suiteIcon: route.icon || '',
    themeColor: route.color || '#3b82f6',
  };
}

/**
 * Generates Angular routes for all children of a root path,
 * merging them with a provided component map.
 */
export function generateRoutes(rootPath: string, componentMap: Record<string, any>): any[] {
  const rootRoute = CORE_APP_ROUTES.find((r) => r.path === rootPath);
  if (!rootRoute || !rootRoute.children) {
    return [];
  }

  return rootRoute.children.map((child) => {
    const slug = child.path.split('/').pop() || '';
    const routeData: any = {
      path: slug,
      data: {
        title: child.title,
        description: child.seoMetadata?.description || child.description,
        keywords: child.seoMetadata?.keywords?.join(', ') || child.keywords?.join(', '),
        icon: child.icon,
        color: child.color,
        comingSoon: child.comingSoon,
        inputs: child.inputs,
        outputs: child.outputs,
      },
    };

    const mapped = componentMap[slug];
    if (mapped) {
      // Check if it's a lazy loading function (simple heuristic)
      if (typeof mapped === 'function' && !mapped.prototype) {
        routeData.loadComponent = mapped;
      } else {
        routeData.component = mapped;
      }
    }

    return routeData;
  });
}
